/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { ColDef, ValueGetterParams, ValueSetterParams } from '@ag-grid-community/core';
import {
  Feature,
  FeatureChoice,
  JobOrder,
  JobOrderDetail,
  JobOrderDetailAddon,
  JobOrderHistDetail,
  KitDetail,
  PricedProductTypeFeatureChoice,
  Product,
  ProductType,
  ProductTypeConfigAddon,
  ProductTypeConfigProduct,
} from '@models';
import { UtilFns } from '@utils';
import * as _ from 'lodash-es';
import { AgFns } from '../components';

type GetProductFn = (params: any) => Product;
type GetJobOrderDetailFn = (params: any) => JobOrderDetail;

type FormatFeatureChoiceFn = (product: Product, featureName: string, featureChoiceValue: string) => string;

export interface JobOrderPriceInfo {
  jobOrder: JobOrder;
}

export interface JobOrderDetailPriceInfo {
  jobOrderDetail: JobOrderDetail;
  productTypeConfigProduct: ProductTypeConfigProduct;

  // Not available on version with data from JobOrderDetailPrice
  pricedProductTypeFeatureChoices: PricedProductTypeFeatureChoice[];
  jobOrderDetailAddons: JobOrderDetailAddon[];
  basePrice: number;
  totalFeaturesPrice: number;
  totalAddonsPrice: number;
  totalUnitPrice: number;
  extendedPrice: number;
  returnPolicyId: string;
  reissuancePrice?: number;
}

export interface ProductTypeConfigPriceInfo {
  pricedProductTypeFeatureChoices: PricedProductTypeFeatureChoice[];
  // productTypeConfigAddons: ProductTypeConfigAddon[]
  basePrice: number;
  totalFeaturesPrice: number;
  returnPolicyId: string;
  reissuancePrice?: number;
}

export interface KitDetailPriceInfo {
  kitDetail: KitDetail;
  productTypeConfigProduct: ProductTypeConfigProduct;

  pricedProductTypeFeatureChoices: PricedProductTypeFeatureChoice[];
  kitDetailAddons: JobOrderDetailAddon[];
  basePrice: number;
  overrideKitUnitPriceAmt: number;
  actualBasePrice: number;
  totalFeaturesPrice: number;
  totalAddonsPrice: number;
  totalUnitPrice: number;
  extendedPrice: number;
}

export class ProductFns {
  static getJobOrderDetailPriceInfo(jod: JobOrderDetail) {
    if (jod.productTypeConfigProduct == null) {
      return undefined;
    }
    const jodPriceInfo = {} as JobOrderDetailPriceInfo;
    jodPriceInfo.jobOrderDetail = jod;
    jodPriceInfo.jobOrderDetailAddons = jod.jobOrderDetailAddons;

    const ptcPriceInfo = this.getProductTypeConfigPriceInfo(jod.productTypeConfigProduct);
    jodPriceInfo.returnPolicyId = ptcPriceInfo.returnPolicyId;
    // producTypeConfigAddons no longer needed - but needed to use it to isolate the rest of ptcPriceInfo.
    Object.assign(jodPriceInfo, ptcPriceInfo);
    jodPriceInfo.totalAddonsPrice = _.sum(jod.jobOrderDetailAddons.map((x) => x.pricedAddon.price));

    jodPriceInfo.totalUnitPrice = jodPriceInfo.basePrice + jodPriceInfo.totalFeaturesPrice + jodPriceInfo.totalAddonsPrice;
    jodPriceInfo.extendedPrice = jodPriceInfo.totalUnitPrice * jod.qty;
    jodPriceInfo.reissuancePrice = ptcPriceInfo.reissuancePrice;
    return jodPriceInfo;
  }

  static getKitDetailPriceInfo(kitDetail: KitDetail) {
    if (kitDetail.productTypeConfigProduct == null) {
      return undefined;
    }
    const kitDetailPriceInfo = {} as KitDetailPriceInfo;
    kitDetailPriceInfo.kitDetail = kitDetail;
    kitDetailPriceInfo.productTypeConfigProduct = kitDetail.productTypeConfigProduct;

    const ptcPriceInfo = this.getProductTypeConfigPriceInfo(kitDetail.productTypeConfigProduct);

    kitDetailPriceInfo.basePrice = ptcPriceInfo.basePrice;
    kitDetailPriceInfo.overrideKitUnitPriceAmt = <number>kitDetail.productTypeConfigProduct.getKitOverridePrice();
    kitDetailPriceInfo.actualBasePrice = <number>kitDetail.productTypeConfigProduct.getActualUnitPrice();
    kitDetailPriceInfo.totalFeaturesPrice = <number>kitDetail.productTypeConfigProduct.getExtraPrice();
    kitDetailPriceInfo.totalAddonsPrice = <number>kitDetail.productTypeConfigProduct.getAddonsPrice();
    kitDetailPriceInfo.totalUnitPrice =
      kitDetailPriceInfo.actualBasePrice + kitDetailPriceInfo.totalFeaturesPrice + kitDetailPriceInfo.totalAddonsPrice;
    kitDetailPriceInfo.extendedPrice = kitDetailPriceInfo.totalUnitPrice * kitDetail.quantity;
    return kitDetailPriceInfo;
  }

  static getProductTypeConfigPriceInfo(ptcProduct: ProductTypeConfigProduct) {
    const ptc = ptcProduct.productTypeConfig;
    const basePrice = ptc.pricedProductType.basePrice;
    const allPptFeatureChoices = ptc.pricedProductType.pricedProductTypeFeatureChoices;

    const product = ptcProduct.product;
    // PricedProductTypeFeatureChoices is the upcharge table — a row exists only when
    // an admin set a non-$0 extra price. Standard colors and base sizes legitimately
    // have no row and contribute $0 to the feature price. See memory:
    // sql-proximity-availability-vs-upcharge.
    const pptFeatureChoices: PricedProductTypeFeatureChoice[] = [];
    product.productFeatureChoices.forEach((pfc) => {
      const pptFeatureChoice = allPptFeatureChoices.find((x) => x.featureChoiceId == pfc.featureChoiceId);
      if (pptFeatureChoice != null) {
        pptFeatureChoices.push(pptFeatureChoice);
      }
    });
    const totalFeaturesPrice = _.sum(pptFeatureChoices.map((x) => x.price));

    return {
      pricedProductTypeFeatureChoices: pptFeatureChoices,
      basePrice: basePrice,
      totalFeaturesPrice: totalFeaturesPrice,
      returnPolicyId: ptc.returnPolicyId,
      reissuancePrice: ptc.pricedProductType.reissuancePrice,
    } as ProductTypeConfigPriceInfo;
  }

  static getAllFeaturesColDef(getProductFn: GetProductFn = (params) => params.data as Product) {
    return {
      headerName: 'All Features',
      colId: 'f-all-features',
      editable: false,
      sortable: false,
      ...AgFns.flexWidth(180),
      valueGetter: (params) => {
        const prod = getProductFn(params);
        if (prod == null) return '';
        const s = prod
          .getOrderedProductFeatureChoices()
          .map((pfc) => pfc.featureChoice.feature.getDisplayName() + ': ' + pfc.featureChoice.validValue)
          .join(', ');
        // const s =  prod.productFeatureChoices.map(pfc => pfc.featureChoice.feature.getDisplayName() + ': ' + pfc.featureChoice.validValue).join(', ');
        return s;
      },
    };
  }

  static getJobOrderDetailLabelColDef(
    getJobOrderDetailFn: GetJobOrderDetailFn = (params) => params.data as JobOrderDetail,
    maxWidth: number = 70,
  ) {
    return {
      headerName: '',
      colId: 'f-jod',
      editable: false,
      sortable: false,
      maxWidth: maxWidth,
      valueGetter: (params) => {
        const jod = getJobOrderDetailFn(params);
        if (jod == null) return '';
        if (jod.programKitId) {
          return 'Kit';
        } else {
          return 'Edit';
        }
      },
    };
  }

  static getAllFeaturesFromProduct(product: Product) {
    if (!product) return '';
    return product
      .getOrderedProductFeatureChoices()
      .map((pfc) => pfc.featureChoice.feature.getDisplayName() + ': ' + pfc.featureChoice.validValue)
      .join(', ');
    // return product.productFeatureChoices.map(pfc => pfc.featureChoice.feature.getDisplayName() + ': ' + pfc.featureChoice.validValue).join(', ');
  }

  // Mirrors server-side JobOrderAdjustmentService.FormatProductDescription so that
  // client-written JobOrderNotes describe products identically to server-written ones.
  static formatProductDescription(johd: JobOrderHistDetail | null | undefined): string {
    if (!johd) return '';
    const productTypeName = johd.product?.productType?.name ?? johd.productTypeConfigName ?? '';
    const featureStr = ProductFns.getAllFeaturesFromProduct(johd.product);
    if (!featureStr) return productTypeName;
    return productTypeName ? `${productTypeName}.  ${featureStr}` : featureStr;
  }

  static getColDefsForProductType(productType?: ProductType, getProductFn: GetProductFn = (params) => params.data as Product) {
    if (productType == null) {
      return [] as ColDef[];
    }
    const orderedFeatures = _.orderBy(productType.productTypeFeatures, (x) => x.displayOrder).map((ptf) => ptf.feature);
    const colDefs = this.getFeatureColDefs(orderedFeatures, getProductFn);
    return colDefs;
  }

  static getFeatureColDefs(features: Feature[], getProductFn: GetProductFn = (params) => params.data as Product) {
    const featureColDefs = features.map((x) => this.createFeatureColDef(x, getProductFn));
    return featureColDefs;
  }

  static createFeatureColDef(feature: Feature, getProductFn: GetProductFn = (params) => params.data as Product) {
    return {
      headerName: feature?.getDisplayName(),
      colId: 'f' + feature?.name,
      ...AgFns.fixWidth(130),
      editable: false,
      sortable: false,
      valueGetter: (params) => {
        const prod = getProductFn(params);
        if (prod == null) return;
        const pfc = prod.productFeatureChoices.find((fc) => fc.featureChoice?.featureId == feature.id);
        return pfc?.featureChoice.validValue ?? '';
      },
    } as ColDef;
  }
}
